import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Clock, CalendarDays, CalendarRange } from "lucide-react";
import { WorkRow } from "../components/mywork/WorkRow";
import { DueReasonDialog } from "../components/dashboard/todos/DueReasonDialog";
import { initAuthToken, TOKEN_KEY, isTokenExpired } from "../lib/authToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTER_KEY = "equipr-my-work-filter";
const MANROPE = { fontFamily: "Manrope, sans-serif" };

const BUCKETS = [
  { key: "overdue", label: "Overdue", ink: "#D24317", Icon: AlertTriangle, empty: "Nothing overdue." },
  { key: "today", label: "Today", ink: "#8A6D00", Icon: Clock, empty: "Nothing due today." },
  { key: "this_week", label: "This week", ink: "#59554D", Icon: CalendarDays, empty: "Nothing else this week." },
  { key: "later", label: "Later", ink: "#59554D", Icon: CalendarRange, empty: "Nothing scheduled later." },
];

const bucketOf = (item, today, weekEnd) => {
  const d = item.due_date;
  if (!d) return item.source === "mention" ? "today" : "later";
  if (d < today) return "overdue";
  if (d === today) return "today";
  if (d <= weekEnd) return "this_week";
  return "later";
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const dayLabel = (iso, long) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  const wd = long ? DAYS[d.getDay()] : DAYS[d.getDay()].slice(0, 3);
  return `${wd} ${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`;
};

// only Anthony can move a due date without saying why
const dueReasonExempt = () => {
  try { return (JSON.parse(localStorage.getItem("equipr_session") || "{}").id || "") === "anthony"; }
  catch { return false; }
};

export default function MyWorkPage() {
  const [data, setData] = useState(null);
  const [dueReason, setDueReason] = useState(null);
  const [items, setItems] = useState([]);
  const [ui, setUi] = useState({});
  const [removed, setRemoved] = useState(() => new Set());
  const [filter, setFilter] = useState(() => localStorage.getItem(FILTER_KEY) || "all");
  const [doneCount, setDoneCount] = useState(0);
  const [syncedAt, setSyncedAt] = useState(null);
  const [failing, setFailing] = useState(false);
  const [ago, setAgo] = useState(0);
  const uiRef = useRef(ui);
  uiRef.current = ui;
  const toastRef = useRef({});
  const undoneRef = useRef(new Set());

  useEffect(() => {
    initAuthToken();
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t || isTokenExpired(t)) window.location.href = "/login";
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/my-work/inbox`);
      setData(r.data);
      setItems(r.data.items || []);
      setSyncedAt(Date.now());
      setFailing(false);
    } catch (e) {
      setFailing(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [load]);
  useEffect(() => {
    const iv = setInterval(() => setAgo(syncedAt ? Math.round((Date.now() - syncedAt) / 1000) : 0), 1000);
    return () => clearInterval(iv);
  }, [syncedAt]);

  const today = data?.today || "";
  const weekEnd = data?.week_end || "";
  const live = items.filter((i) => !removed.has(i.id));
  const visible = filter === "all" ? live : live.filter((i) => i.chip === filter);
  const counts = BUCKETS.reduce((acc, b) => ({ ...acc, [b.key]: 0 }), {});
  live.forEach((i) => { counts[bucketOf(i, today, weekEnd)] += 1; });
  const chipCounts = {};
  (data?.chips || []).forEach((c) => { chipCounts[c.key] = live.filter((i) => i.chip === c.key).length; });

  useEffect(() => {
    const n = (counts.overdue || 0) + (counts.today || 0);
    document.title = n > 0 ? `(${n}) My Work · EQUIPR` : "My Work · EQUIPR";
  }, [counts.overdue, counts.today]);

  const setItemUi = (id, patch) => setUi((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));
  const onToggle = (id, patch, exclusive) => {
    if (exclusive && patch.comment) {
      setUi((p) => {
        const next = {};
        Object.entries(p).forEach(([k, v]) => { next[k] = { ...v, comment: false }; });
        return { ...next, [id]: { ...(p[id] || {}), ...patch } };
      });
      return;
    }
    setItemUi(id, patch);
  };

  const pick = (item) => uiRef.current[item.id] || {};

  const onComplete = async (item) => {
    setItemUi(item.id, { busy: true });
    try {
      await axios.post(`${API}/my-work/complete`, { item_id: item.id });
      setItemUi(item.id, { busy: false, done: true });
      setDoneCount((n) => n + 1);
      const verb = { Done: "done", Approve: "approved", Resolve: "resolved", "Check in": "checked in" }[item.completion] || "done";
      const short = item.title.length > 48 ? `${item.title.slice(0, 48)}…` : item.title;
      const drop = () => { if (!undoneRef.current.has(item.id)) setRemoved((p) => new Set(p).add(item.id)); };
      const tid = toast(`Marked ${verb}: ${short}`, {
        duration: 5000,
        position: "bottom-center",
        action: { label: "Undo", onClick: () => onUndo(item) },
        onAutoClose: drop,
        onDismiss: drop,
      });
      undoneRef.current.delete(item.id);
      toastRef.current[item.id] = tid;
    } catch (e) {
      setItemUi(item.id, { busy: false });
      toast.error(e?.response?.data?.detail || "That didn't save.", {
        action: { label: "Retry", onClick: () => onComplete(item) },
      });
    }
  };

  const onUndo = async (item) => {
    try {
      await axios.post(`${API}/my-work/undo`, { item_id: item.id });
      // stop the completion toast from dropping the row we just restored
      undoneRef.current.add(item.id);
      if (toastRef.current[item.id] !== undefined) {
        toast.dismiss(toastRef.current[item.id]);
        delete toastRef.current[item.id];
      }
      setRemoved((p) => { const n = new Set(p); n.delete(item.id); return n; });
      setItemUi(item.id, { done: false });
      setDoneCount((n) => Math.max(0, n - 1));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't undo that.");
    }
  };

  const onDate = async (item, due, due_reason) => {
    const prev = item.due_date;
    if (prev && !dueReasonExempt() && !due_reason && prev.slice(0, 10) !== String(due).slice(0, 10)) {
      setDueReason({
        from: prev, to: due, count: 1,
        onConfirm: async (reason) => { setDueReason(null); await onDate(item, due, reason); },
      });
      return;
    }
    setItems((list) => list.map((i) => (i.id === item.id ? { ...i, due_date: due } : i)));
    setItemUi(item.id, { editingDate: false });
    try {
      await axios.post(`${API}/my-work/date`, { item_id: item.id, due_date: due, due_reason });
    } catch (e) {
      setItems((list) => list.map((i) => (i.id === item.id ? { ...i, due_date: prev } : i)));
      toast.error(e?.response?.data?.detail || "The date didn't save.", {
        action: { label: "Retry", onClick: () => onDate(item, due, due_reason) },
      });
    }
  };

  const onComment = async (item, text) => {
    try {
      const r = await axios.post(`${API}/my-work/comment`, { item_id: item.id, text });
      setItems((list) => list.map((i) => (i.id === item.id
        ? { ...i, comments_count: r.data.comments_count ?? (i.comments_count + 1), last_comment: { author: "You", text } }
        : i)));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The comment didn't post.", {
        action: { label: "Retry", onClick: () => onComment(item, text) },
      });
      return false;
    }
  };

  const chooseFilter = (k) => { setFilter(k); localStorage.setItem(FILTER_KEY, k); };

  const rowsFor = (key) => visible
    .filter((i) => bucketOf(i, today, weekEnd) === key)
    .sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));

  return (
    <div className="min-h-screen bg-[#EDEAE3]" data-testid="my-work-page">
      <header className="h-[52px] bg-[#121212] flex items-center justify-between px-6">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-[15px] font-extrabold whitespace-nowrap" style={MANROPE}>
            <span className="text-[#F5F5F5]">EQUIPR </span><span className="text-[#FFD108]">INTELLIGENCE</span>
          </span>
          <span className="mono text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-[#FFD108] text-[#121212] whitespace-nowrap">MY WORK</span>
        </div>
        <div className="flex items-center gap-4 min-w-0">
          <span className="flex items-center gap-2 text-[12px] text-[#B8B4AC] whitespace-nowrap" data-testid="mw-live">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: failing ? "#E0B800" : "#2E8B52" }} />
            <span className="hidden sm:inline">
              {failing ? "Reconnecting…" : ago < 5 ? "Live · updated just now" : `Live · updated ${ago}s ago`}
            </span>
          </span>
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-7 h-7 rounded-full bg-[#FFD108] text-[#121212] text-[11px] font-bold flex items-center justify-center shrink-0">
              {data?.user?.initials || "?"}
            </span>
            <span className="hidden sm:inline text-[13px] text-[#F5F5F5] truncate">{data?.user?.name || ""}</span>
          </span>
        </div>
      </header>

      <div className="mx-auto px-6 py-7" style={{ maxWidth: 1180 }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[26px] font-bold text-[#1A1A1A] tracking-[-0.018em]" style={MANROPE} data-testid="mw-title">My Work</h1>
            <p className="mt-1 text-[14px] text-[#59554D]" data-testid="mw-summary">
              {dayLabel(today, true)} · <span className="font-semibold" style={{ color: counts.overdue ? "#D24317" : "#59554D" }}>{counts.overdue} overdue</span>
              {` · ${counts.today} today · ${counts.this_week} this week · ${counts.later} later`}
            </p>
          </div>
          {doneCount > 0 && (
            <span className="text-[12.5px] text-[#8A857C]" data-testid="mw-session">{doneCount} done this session</span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="mw-chips">
          {[{ key: "all", label: "All" }, ...(data?.chips || [])].map((c) => {
            const count = c.key === "all" ? live.length : (chipCounts[c.key] || 0);
            const active = filter === c.key;
            return (
              <button key={c.key} onClick={() => chooseFilter(c.key)} data-testid={`mw-chip-${c.key}`}
                      className={`inline-flex items-center gap-2 h-8 px-3 rounded-full text-[12.5px] font-semibold transition-colors duration-150 ${active ? "" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:text-[#1A1A1A]"}`}
                      style={{ ...(active ? { background: "#121212", color: "#FFFFFF" } : {}), opacity: count === 0 && !active ? 0.55 : 1 }}>
                {c.label}
                <span className="mono text-[11px]" style={{ color: active ? "#FFD108" : "#8A857C" }}>{count}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-5 space-y-4">
          {!data ? (
            BUCKETS.map((b) => (
              <div key={b.key} className="bg-white border border-[#DAD5CA] rounded-[10px] overflow-hidden">
                <div className="h-[42px] bg-[#FBFAF7]" />
                {[0, 1, 2].map((i) => (
                  <div key={i} className="px-5 py-3.5 border-t border-[#EFECE4] flex items-center gap-4">
                    <div className="w-9 h-9 rounded-[9px] bg-[#F1EEE6] animate-pulse" />
                    <div className="flex-1 h-9 bg-[#F1EEE6] rounded animate-pulse" />
                    <div className="w-24 h-9 bg-[#F1EEE6] rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ))
          ) : BUCKETS.map((b) => {
            const rows = rowsFor(b.key);
            return (
              <div key={b.key} className="bg-white border border-[#DAD5CA] rounded-[10px] overflow-hidden"
                   data-testid={`mw-bucket-${b.key}`}>
                <div className="flex items-center gap-2 px-5 py-2.5 bg-[#FBFAF7]">
                  <b.Icon className="w-4 h-4" style={{ color: b.ink }} />
                  <span className="text-[13.5px] font-bold" style={{ ...MANROPE, color: b.ink }}>{b.label}</span>
                  {b.key === "today" && <span className="text-[12px] text-[#8A857C]">{dayLabel(today)}</span>}
                  {b.key === "this_week" && <span className="text-[12px] text-[#8A857C]">to {dayLabel(weekEnd)}</span>}
                  <span className="mono text-[11.5px] text-[#8A857C] ml-auto" data-testid={`mw-count-${b.key}`}>{rows.length}</span>
                </div>
                {rows.length === 0 ? (
                  <div className="px-5 py-3 text-[13px] text-[#8A857C]">{b.empty}</div>
                ) : rows.map((item) => (
                  <WorkRow key={item.id} item={item} today={today} state={pick(item)}
                           onComplete={onComplete} onUndo={onUndo} onDate={onDate}
                           onComment={onComment} onToggle={onToggle} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
      {dueReason && <DueReasonDialog {...dueReason} onCancel={() => { setDueReason(null); load(); }} />}
    </div>
  );
}
